import { useAuthStore } from '@/lib/stores/authStore';
import { refreshAccessToken } from '@/lib/api/auth';
import { logger } from '../utils/logger';

/**
 * API Configuration
 *
 * New architecture:
 * - Auth API: {VITE_AUTH_API_URL}/auth/login
 * - FHIR API: {VITE_AUTH_API_URL}/api/hmis/fhir/R4
 * - HMIS API: {VITE_AUTH_API_URL}/api/hmis
 */
const AUTH_API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;
const HMIS_API_BASE_URL = `${AUTH_API_BASE_URL}/api/hmis`;
const FHIR_API_BASE_URL = `${HMIS_API_BASE_URL}/fhir/R4`;

// Legacy fallback token for development (deprecated - will be removed in future)
export const FALLBACK_BEARER_TOKEN = import.meta.env.VITE_MEDPLUM_BEARER_TOKEN;

const API_TIMEOUT = 30000; // 30 seconds

/**
 * HTTP Methods
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * API Request Options
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  timeout?: number;
  requiresAuth?: boolean;
  customHeaders?: Record<string, string>;
  body?: unknown; // Allow any type - will be stringified if not FormData or string
  /** Override the default base URL. Use for pranik-hmis BFF /v2 routes. */
  baseUrl?: string;
}

/**
 * API Error class for structured error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: unknown,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

/**
 * Create a timeout promise
 */
function createTimeoutPromise(timeout: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new ApiError('Request timeout', 408));
    }, timeout);
  });
}

function extractOperationOutcomeMessage(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const outcome = data as {
    resourceType?: string;
    issue?: Array<{
      severity?: string;
      code?: string;
      diagnostics?: string;
      details?: { text?: string };
    }>;
  };

  if (outcome.resourceType !== 'OperationOutcome' || !Array.isArray(outcome.issue)) {
    return null;
  }

  for (const issue of outcome.issue) {
    if (issue?.details?.text) {
      return issue.details.text;
    }
    if (issue?.diagnostics) {
      return issue.diagnostics;
    }
  }

  return null;
}

/**
 * Message from the HMIS backend's error envelope (`{ success: false, error, details }`),
 * preferring the specific `details` text over the generic `error` label, or a plain `message`.
 */
function extractBackendErrorMessage(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as { details?: unknown; error?: unknown; message?: unknown };
  for (const value of [body.details, body.error, body.message]) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

/**
 * Parse response body based on content type
 */
async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json') || contentType?.includes('application/fhir+json')) {
    return response.json();
  } else if (contentType?.includes('text/')) {
    return response.text();
  } else if (contentType?.includes('multipart/form-data')) {
    return response.formData();
  } else if (contentType?.includes('application/octet-stream')) {
    return response.blob();
  } else {
    // Try to parse as JSON by default, fallback to text
    try {
      return await response.json();
    } catch {
      return response.text();
    }
  }
}

/**
 * Make an HTTP request with interceptors
 *
 * @param endpoint - API endpoint (relative to base URL)
 * @param method - HTTP method
 * @param options - Request options
 * @returns Promise with API response
 *
 * @throws {ApiError} - On request failure
 */
async function request<T = unknown>(
  endpoint: string,
  method: HttpMethod = 'GET',
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const {
    timeout = API_TIMEOUT,
    requiresAuth = true,
    customHeaders = {},
    body,
    baseUrl: baseUrlOverride,
    ...fetchOptions
  } = options;

  // Determine base URL based on endpoint
  // FHIR resources go to /fhir/R4
  // Other endpoints go to base URL (e.g., /auth/login, /me)
  let baseUrl = baseUrlOverride ?? HMIS_API_BASE_URL;

  // List of FHIR resource types
  const fhirResources = [
    'Appointment',
    'Patient',
    'Practitioner',
    'PractitionerRole',
    'Encounter',
    'ClinicalImpression',
    'DiagnosticReport',
    'Composition',
    'Observation',
    'DocumentReference',
  ];

  if (
    !baseUrlOverride &&
    (endpoint.startsWith('/fhir/') || fhirResources.some((resource) => endpoint.includes(resource)))
  ) {
    baseUrl = FHIR_API_BASE_URL;
    // Remove /fhir prefix if present since it's already in the base URL
    if (endpoint.startsWith('/fhir/')) {
      endpoint = endpoint.substring(5); // Remove '/fhir'
    }
  }

  // Build full URL
  const url = `${baseUrl}${endpoint}`;

  // BFF override routes use application/json; FHIR uses application/fhir+json
  const defaultContentType = baseUrlOverride ? 'application/json' : 'application/fhir+json';

  // Build headers
  const headers: HeadersInit = {
    'Content-Type': defaultContentType,
    ...customHeaders,
  };

  // Add authentication token if required
  if (requiresAuth) {
    let token = useAuthStore.getState().token;

    if ((!token || token.length === 0) && typeof FALLBACK_BEARER_TOKEN === 'string') {
      token = FALLBACK_BEARER_TOKEN;
    }

    if (token && token.length > 0) {
      headers['Authorization'] = token.startsWith('Bearer') ? token : `Bearer ${token}`;
    }
  }

  // Build request options
  // credentials: 'include' allows browser to send/receive HTTP-Only cookies
  const requestOptions: RequestInit = {
    method,
    headers,
    credentials: 'include',
    ...fetchOptions,
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    if (body instanceof FormData) {
      requestOptions.body = body;
      // Remove Content-Type header to let browser set it with boundary
      delete (headers as Record<string, string>)['Content-Type'];
    } else if (typeof body === 'string') {
      requestOptions.body = body;
    } else {
      requestOptions.body = JSON.stringify(body);
    }
  }

  try {
    // Make request with timeout
    const fetchPromise = fetch(url, requestOptions);
    const response = await Promise.race([fetchPromise, createTimeoutPromise(timeout)]);

    // Parse response body
    const data = await parseResponseBody(response);

    // Handle HTTP errors
    if (!response.ok) {
      const errorMessage =
        extractOperationOutcomeMessage(data) ||
        extractBackendErrorMessage(data) ||
        response.statusText ||
        'Request failed';

      // Handle 401 Unauthorized - try to refresh token before logging out
      if (response.status === 401 && requiresAuth) {
        // 401 received - attempting token refresh

        try {
          const newToken = await refreshAccessToken({ clearAuthOnFailure: false });

          if (newToken) {
            // Token refresh successful - retrying request

            // Update the authorization header with the new token
            const retryHeaders: HeadersInit = {
              ...headers,
              Authorization: `Bearer ${newToken}`,
            };

            // Retry the request with the new token
            const retryOptions: RequestInit = {
              ...requestOptions,
              headers: retryHeaders,
            };

            const retryFetchPromise = fetch(url, retryOptions);
            const retryResponse = await Promise.race([
              retryFetchPromise,
              createTimeoutPromise(timeout),
            ]);
            const retryData = await parseResponseBody(retryResponse);

            if (retryResponse.ok) {
              // Retry request successful
              return {
                data: retryData as T,
                status: retryResponse.status,
                statusText: retryResponse.statusText,
                headers: retryResponse.headers,
              };
            } else {
              // Retry failed - extract error and throw
              const retryErrorMessage =
                extractOperationOutcomeMessage(retryData) ||
                (typeof retryData === 'object' && retryData !== null && 'message' in retryData
                  ? (retryData as { message: string }).message
                  : retryResponse.statusText || 'Request failed');

              // CRITICAL FIX: Do NOT call clearAuth() here.
              // If refresh fails, we let the request fail with an error, but keep the session alive.
              // This prevents users from being logged out due to transient network issues or
              // temporary backend failures. Users can retry their action or refresh the page.
              // The proactive refresh hook (useTokenRefresh) will continue attempting to refresh.
              if (retryResponse.status === 401) {
                logger.warn('Request failed with 401 after token refresh - session kept alive');
              }

              throw new ApiError(retryErrorMessage, retryResponse.status, retryData);
            }
          } else {
            // Token refresh returned null - either no refresh token or refresh failed
            // clearAuth is only called when the refresh API fails, not when there's no token
            // Token refresh returned null - cannot retry
          }
        } catch {
          // Token refresh error - will fall through to original error
        }
      }

      throw new ApiError(errorMessage, response.status, data);
    }

    // Return successful response
    return {
      data: data as T,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }

    // Wrap other errors
    if (error instanceof Error) {
      throw new ApiError(error.message || 'Network error', undefined, undefined, error);
    }

    // Unknown error
    throw new ApiError('An unexpected error occurred');
  }
}

/**
 * API Client
 *
 * Provides a simple interface for making HTTP requests with automatic:
 * - Token injection for authenticated requests
 * - Error handling with structured errors
 * - Request timeouts
 * - Response parsing
 * - 401 handling (auto-logout)
 *
 * @example
 * ```tsx
 * // GET request
 * const response = await apiClient.get<User[]>('/users');
 * logger.log(response.data);
 *
 * // POST request
 * const response = await apiClient.post<User>('/users', {
 *   body: { name: 'John Doe', email: 'john@example.com' }
 * });
 *
 * // Unauthenticated request
 * const response = await apiClient.post('/auth/login', {
 *   requiresAuth: false,
 *   body: { username: 'admin', password: 'password' }
 * });
 *
 * // Error handling
 * try {
 *   const response = await apiClient.get('/protected');
 * } catch (error) {
 *   if (error instanceof ApiError) {
 *     console.error(`API Error: ${error.message} (${error.statusCode})`);
 *   }
 * }
 * ```
 */
export const apiClient = {
  /**
   * Make a GET request
   */
  get: <T = unknown>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, 'GET', options),

  /**
   * Make a POST request
   */
  post: <T = unknown>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, 'POST', options),

  /**
   * Make a PUT request
   */
  put: <T = unknown>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, 'PUT', options),

  /**
   * Make a PATCH request
   */
  patch: <T = unknown>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, 'PATCH', options),

  /**
   * Make a DELETE request
   */
  delete: <T = unknown>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, 'DELETE', options),

  /**
   * Get the FHIR base URL
   */
  getFhirBaseUrl: () => FHIR_API_BASE_URL,

  /**
   * Get the HMIS API base URL
   */
  getHmisBaseUrl: () => HMIS_API_BASE_URL,

  /**
   * Get the Auth API base URL
   */
  getAuthBaseUrl: () => AUTH_API_BASE_URL,
};

/**
 * Helper to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Shown when the server cannot be reached at all (offline, DNS, CORS, blocked). */
export const NETWORK_ERROR_MESSAGE =
  "We couldn't reach the server. Please check your internet connection and try again.";

const NETWORK_FAILURE_PATTERN = /failed to fetch|networkerror|load failed|network request failed/i;

/**
 * Turn any thrown value into a message that is safe to show to a user.
 * Backend wording passes through for client-correctable errors (400/404/409/
 * 422); connectivity, auth, permission and server failures get fixed,
 * actionable text so raw strings like "Failed to fetch" or "Forbidden" never
 * reach the screen.
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    switch (error.statusCode) {
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return "You don't have permission to do this. Please contact your administrator.";
      case 408:
        return 'The server took too long to respond. Please try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      default:
        if (error.statusCode && error.statusCode >= 500) {
          return 'The server ran into a problem. Please try again in a moment.';
        }
        return error.message;
    }
  }
  if (error instanceof Error) {
    if (error instanceof TypeError || NETWORK_FAILURE_PATTERN.test(error.message)) {
      return NETWORK_ERROR_MESSAGE;
    }
    return error.message;
  }
  return 'An unexpected error occurred';
}
